import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type PaymentIntentTriggerSource =
  | 'gallery_card_click'
  | 'gallery_see_another'
  | 'chat_trigger'
  | 'chat_see_another';

interface PaymentIntentContextValue {
  recommendationInteractionCount: number;
  isPaymentIntentModalOpen: boolean;
  hasShownPaymentIntentModal: boolean;
  lastModalTriggerSource: PaymentIntentTriggerSource | null;
  lastModalInteractionCount: number | null;
  lastModalOpenedAt: number | null;
  recordRecommendationInteraction: (source: PaymentIntentTriggerSource) => 'none' | 'threshold_reached';
  resolvePaymentIntentModal: (choice: 'accept' | 'decline') => void;
}

const PaymentIntentContext = createContext<PaymentIntentContextValue | undefined>(undefined);

interface PaymentIntentProviderProps {
  children: ReactNode;
}

export const PaymentIntentProvider: React.FC<PaymentIntentProviderProps> = ({ children }) => {
  const { email } = useAuth();
  const [recommendationInteractionCount, setRecommendationInteractionCount] = useState(0);
  const [isPaymentIntentModalOpen, setIsPaymentIntentModalOpen] = useState(false);
  const [hasShownPaymentIntentModal, setHasShownPaymentIntentModal] = useState(false);
  const [lastModalTriggerSource, setLastModalTriggerSource] = useState<PaymentIntentTriggerSource | null>(null);
  const [lastModalInteractionCount, setLastModalInteractionCount] = useState<number | null>(null);
  const [lastModalOpenedAt, setLastModalOpenedAt] = useState<number | null>(null);

  // localStorage key
  const storageKey = email ? `payment_intent_state_${email}` : null;

  // 初始化：从 localStorage 获取当前账号的交互次数和是否弹过问卷
  useEffect(() => {
    if (!storageKey) {
      // 没有 email 时保持默认 0/false 状态
      setRecommendationInteractionCount(0);
      setHasShownPaymentIntentModal(false);
      return;
    }

    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) {
        setRecommendationInteractionCount(0);
        setHasShownPaymentIntentModal(false);
        return;
      }
      const data = JSON.parse(raw) as {
        recommendationInteractionCount?: number;
        hasShownPaymentIntentModal?: boolean;
      };
      setRecommendationInteractionCount(
        typeof data.recommendationInteractionCount === 'number'
          ? data.recommendationInteractionCount
          : 0
      );
      setHasShownPaymentIntentModal(!!data.hasShownPaymentIntentModal);
    } catch {
      // 解析失败时清空本地状态，避免错误影响体验
      window.localStorage.removeItem(storageKey);
      setRecommendationInteractionCount(0);
      setHasShownPaymentIntentModal(false);
    }
  }, [storageKey]);

  const syncIntentState = (nextCount: number, nextHasShown: boolean) => {
    if (!storageKey) return;
    const payload = {
      recommendationInteractionCount: nextCount,
      hasShownPaymentIntentModal: nextHasShown,
    };
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(payload));
    } catch {
      // localStorage 写失败时静默忽略
    }
  };

  const recordRecommendationInteraction = (source: PaymentIntentTriggerSource) => {
    // 如果已经弹过，就不再计数，也不再弹
    if (hasShownPaymentIntentModal) return 'none';

    const next = recommendationInteractionCount + 1;
    setRecommendationInteractionCount(next);

    // 达到或超过阈值：立刻打开 modal，并标记为已展示
    if (next >= 3) {
      setIsPaymentIntentModalOpen(true);
      setHasShownPaymentIntentModal(true);
      setLastModalTriggerSource(source);
      setLastModalInteractionCount(next);
      setLastModalOpenedAt(Date.now());
      syncIntentState(next, true); // 异步 fire-and-forget
      return 'threshold_reached';
    }

    // 未达到阈值，仅更新计数
    syncIntentState(next, false);
    return 'none';
  };

  const resolvePaymentIntentModal = (choice: 'accept' | 'decline') => {
    // 只负责关闭 modal，具体 choice 的后续行为由调用方处理
    setIsPaymentIntentModalOpen(false);
    // hasShownPaymentIntentModal 已经在到达阈值时置为 true，无需在此重复
  };

  const value: PaymentIntentContextValue = {
    recommendationInteractionCount,
    isPaymentIntentModalOpen,
    hasShownPaymentIntentModal,
    lastModalTriggerSource,
    lastModalInteractionCount,
    lastModalOpenedAt,
    recordRecommendationInteraction,
    resolvePaymentIntentModal,
  };

  return (
    <PaymentIntentContext.Provider value={value}>
      {children}
    </PaymentIntentContext.Provider>
  );
};

export const usePaymentIntent = (): PaymentIntentContextValue => {
  const ctx = useContext(PaymentIntentContext);
  if (!ctx) {
    throw new Error('usePaymentIntent must be used within a PaymentIntentProvider');
  }
  return ctx;
};


